import React, { useEffect } from "react";
import headerImage from "./assets/vitrine/creator/header.png";
import "./CreatorsInfluencersPage.css";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";

const sections = [
  {
    eyebrow: "section1",
    title: "Delegated Production & Frictionless Co-Creation",
    headline: "Create together, without the mental load or upfront cash flow.",
    subtitle: "Delegate camera work, motion design, or editing to specialized Weavers: AI synchronizes feeds and executes instant revenue splits.",
    challenge: "Flying solo means spending up to 80% of your time on footage logging, editing, and contractor management. Hiring freelancers ties up working capital and introduces administrative bottlenecks and project delays.",
    weavevo: "Assign dedicated slots to vetted Weavers. AI calibrates color grading, smooths audio transitions, and automatically calculates contributor payouts upon project sign-off.",
    impactLead: "Slash production costs by 60% and triple your publishing frequency.",
    impact: "Automated AI revenue sharing eliminates upfront cash requirements: contributors are compensated directly based on content performance or pre-agreed terms. On the network side, each Weaver mobilizes their own audience to distribute the finished asset, transforming a solitary channel into high-leverage cross-promotional reach.",
  },
  {
    eyebrow: "section2",
    title: "Living Brand Partnerships (Enduring Brand Deals)",
    headline: "Turn one-off sponsorships into predictable monthly recurring revenue.",
    subtitle: "Offer brands modular video assets that evolve alongside their launches: update a single slot without ever reshooting the entire piece.",
    challenge: "Traditional sponsored posts disappear within 24 hours in stories or sink down social feeds, rendering campaigns obsolete the moment an advertiser's promotion or product line changes.",
    weavevo: "Sell a Living Weave. Brands can refresh a specific product or promo slot in minutes via a designated Weaver, leaving the rest of the high-performing video untouched.",
    impactLead: "Quadruple advertiser Customer Lifetime Value (LTV).",
    impact: "Replace static one-off gig fees with a predictable monthly retainer (MRR) for ongoing visual maintenance. On the network side, your Weave becomes the brand’s flagship asset across their social platforms, website, and customer communications, funneling their native audience directly to your ecosystem.",
  },
  {
    eyebrow: "section3",
    title: "Real-Time Multi-Angle Event Coverage",
    headline: "Capture events from every angle without spreading yourself thin.",
    subtitle: "Deploy an on-site squad of Weavers: AI stitches the main stage, backstage access, and audience reactions into a live, interactive, monetized loop.",
    challenge: "At trade shows, festivals, or exclusive premieres, a solo creator cannot physically handle main-stage footage, backstage VIP interviews, and audience reactions in real time.",
    weavevo: "Assemble a pop-up field crew of Weavers. AI synchronizes four dedicated slots in real time and automatically disperses coverage earnings directly into each contributor's digital wallet.",
    impactLead: "Sell coverage packages for 2x to 3x standard solo rates.",
    impact: "Monetize a comprehensive visual package with event organizers or via premium access passes—with zero fixed payroll. On the network side, every squad member cross-promotes their slot to their respective followers, turning the Weave into a viral echo chamber where creator and event audiences compound.",
  },
  {
    eyebrow: "section4",
    title: "Self-Updating Evergreen Assets (Guides & Comparisons)",
    headline: "Build long-term assets that never expire.",
    subtitle: "Keep your top picks, city guides, and product comparisons evergreen: let local Weavers refresh recommendations without rebuilding from scratch.",
    challenge: "Travel guides and tech comparisons lose relevance within months. Broken links and closed venues trigger steep drops in SEO rankings, eroding affiliate commissions by over 70%.",
    weavevo: "Contract local Weavers to refresh individual outdated slots rather than shooting a full new video. AI rebalances the visual loop and keeps the asset fresh, indexed, and active.",
    impactLead: "Preserve 100% of affiliate revenue over a 12-to-24-month horizon.",
    impact: "Slot-level micro-updates cost a fraction of a full production run. On the network side, you build a decentralized network of local correspondents: featured venues and brands actively promote the Weave, driving continuous high-intent traffic without ad spend.",
  },
  {
    eyebrow: "section5",
    title: "Monetized Community Co-Creation (Vetted UGC)",
    headline: "Transform your audience into a paid production studio.",
    subtitle: "Incorporate your followers' best submissions into official Weaves: AI curates the footage and distributes fair, automated payouts.",
    challenge: "Generating high-volume content to feed social algorithms is expensive, while outsourcing to UGC agencies requires hefty upfront budgets with no guarantee of authentic engagement.",
    weavevo: "Run open contribution calls where your community acts as Weavers. AI analyzes incoming submissions, filters top-tier entries, and automatically routes a percentage of view and sponsor revenue to approved creators.",
    impactLead: "Cut content acquisition costs by 75%.",
    impact: "Capital outlay is strictly tied to genuine content monetization. On the network side, viral loop effects ignite instantly: community members featured in a slot become dedicated brand ambassadors, proudly sharing the Weave with their personal networks and multiplying organic reach.",
  },
];

function Section({ section, reverse = false }) {
  return (
    <section className={`creator-section ${reverse ? "creator-sectionReverse" : ""}`}>
      <div className="creator-card">
        <span className="creator-eyebrow">{section.eyebrow}</span>
        <h2>{section.title}</h2>
        <h3>{section.headline}</h3>
        <p className="creator-subtitle">{section.subtitle}</p>

        <div className="creator-divider" />

        <div className="creator-block">
          <strong>The Challenge:</strong>
          <p>{section.challenge}</p>
        </div>

        <div className="creator-block">
          <strong>Enter Weavevo:</strong>
          <p>{section.weavevo}</p>
        </div>

        <div className="creator-block">
          <strong>ROI & Reach:</strong>
          <p className="creator-impactLead">{section.impactLead}</p>
          <p>{section.impact}</p>
        </div>
      </div>
    </section>
  );
}

export default function CreatorsInfluencersPage() {
  useEffect(() => {
    document.title = "Make your content truly living.";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="creator-page">
      <section className="creator-hero">
        <img src={headerImage} alt="" />
        <div className="creator-heroOverlay" />
        <div className="creator-heroContent">
          <p className="creator-heroLabel">Header</p>
          <h1>Make your content truly living.</h1>
          <p className="creator-heroSubtitle">
            Move past static videos that fade in 24 hours. Co-create modular Weaves with local videographers and your community to build evergreen, monetizable media assets.
          </p>
        </div>
      </section>

      <div className="creator-sections">
        {sections.map((section, index) => (
          <Section key={section.title} section={section} reverse={index % 2 === 1} />
        ))}
      </div>

      <section className="creator-cta">
        <div className="creator-ctaInner">
          <span className="creator-ctaEyebrow">Bloc final</span>
          <h2>Turn static sponsorships into living deals today.</h2>
          <p>Join Weavevo to deliver modular, evergreen campaigns for your brand partners. Update video slots in real time and secure recurring revenue in just a few taps.</p>
          <div className="creator-storeActions">
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="creator-storeButton creator-storeButtonPrimary">
              <i className="bi bi-google-play" aria-hidden="true" />
              <span>Play Store</span>
            </a>
            <button type="button" className="creator-storeButton creator-storeButtonSecondary" disabled>
              <i className="bi bi-apple" aria-hidden="true" />
              <span>App Store</span>
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
